/* Mosaic data layer — backed by the encrypted vault.
 *
 * The Mosaic page used to read/write `un_mosaic_v1` from localStorage.
 * Mosaic items can be intimate (photos, songs, things people said) so we
 * now keep them in an AES-GCM-encrypted IndexedDB store.
 *
 * Migration is one-shot and silent: on first call after upgrade, any
 * existing localStorage items are moved into the vault and the
 * localStorage key is cleared.
 */

import {
  isVaultSupported,
  vaultPut,
  vaultList,
  vaultDelete,
  getMeta,
  setMeta,
  STORE_MOSAIC,
  META_KEY_MIGRATED_MOSAIC,
} from "@/lib/secureVault";

const LEGACY_KEY = "un_mosaic_v1";

const uid = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

async function migrateFromLocalStorage() {
  try {
    const done = await getMeta(META_KEY_MIGRATED_MOSAIC);
    if (done) return;
    const raw = localStorage.getItem(LEGACY_KEY);
    if (raw) {
      const items = JSON.parse(raw);
      if (Array.isArray(items)) {
        for (const item of items) {
          // eslint-disable-next-line no-await-in-loop
          await vaultPut(STORE_MOSAIC, item.id || uid(), item);
        }
      }
      localStorage.removeItem(LEGACY_KEY);
    }
    await setMeta(META_KEY_MIGRATED_MOSAIC, true);
  } catch (e) {
    // Migration failure must never block the user. Logged for diagnostics only.
    console.warn("[mosaicVault] legacy migration skipped:", e?.message || e);
  }
}

export async function listMosaic() {
  if (!isVaultSupported()) {
    // Fall back to localStorage if WebCrypto/IDB is missing (very old browsers).
    try {
      const raw = localStorage.getItem(LEGACY_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      console.warn("[mosaicVault] legacy listMosaic fallback failed:", e?.message || e);
      return [];
    }
  }
  await migrateFromLocalStorage();
  const items = await vaultList(STORE_MOSAIC);
  // Newest first by createdAt
  return items.sort((a, b) => {
    const ta = new Date(a.createdAt || 0).getTime();
    const tb = new Date(b.createdAt || 0).getTime();
    return tb - ta;
  });
}

export async function addMosaic(payload) {
  const entry = {
    id: uid(),
    createdAt: new Date().toISOString(),
    ...payload,
  };
  if (!isVaultSupported()) {
    try {
      const raw = localStorage.getItem(LEGACY_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      arr.unshift(entry);
      localStorage.setItem(LEGACY_KEY, JSON.stringify(arr));
    } catch (e) {
      console.warn("[mosaicVault] legacy addMosaic fallback failed:", e?.message || e);
    }
    return entry;
  }
  await migrateFromLocalStorage();
  await vaultPut(STORE_MOSAIC, entry.id, entry);
  return entry;
}

export async function deleteMosaic(id) {
  if (!isVaultSupported()) {
    try {
      const raw = localStorage.getItem(LEGACY_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      const next = arr.filter((m) => m.id !== id);
      localStorage.setItem(LEGACY_KEY, JSON.stringify(next));
    } catch (e) {
      console.warn("[mosaicVault] legacy deleteMosaic fallback failed:", e?.message || e);
    }
    return;
  }
  await vaultDelete(STORE_MOSAIC, id);
}
